import styled from 'styled-components';
import { Warning, EnvelopeSimple } from '@phosphor-icons/react';
import { Container } from '@/components/common/Container';
import { Section } from '@/components/common/Section';
import { Card } from '@/components/common/Card';
import { theme } from '@/styles/theme';

const Content = styled.div`
  max-width: 700px;
  margin: 0 auto;
  text-align: center;

  h1 {
    margin-bottom: ${theme.spacing.xl};
    color: ${theme.colors.text.black};
  }

  p {
    color: ${theme.colors.text.darkGray};
    line-height: 1.8;
    margin-bottom: ${theme.spacing.lg};
  }
`;

const WarningCard = styled(Card)`
  background: #FFF4E6;
  border: 2px solid ${theme.colors.status.warning};
  margin: ${theme.spacing.xxl} 0;
  text-align: left;
`;

const WarningHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.status.warning};
`;

const WarningTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.status.warning};
  margin: 0;
`;

const WarningText = styled.p`
  color: ${theme.colors.text.black};
  margin: 0;
  font-weight: ${theme.fontWeights.medium};
`;

const InstructionsCard = styled(Card)`
  background: ${theme.colors.white};
  margin: ${theme.spacing.xxl} 0;
  text-align: left;
`;

const InstructionTitle = styled.h2`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.black};
  margin-bottom: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const EmailLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  background: ${theme.colors.primary.main};
  color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.large};
  font-weight: ${theme.fontWeights.semibold};
  text-decoration: none;
  transition: all ${theme.transitions.fast};
  margin: ${theme.spacing.lg} 0;

  &:hover {
    background: ${theme.colors.primary.dark};
    transform: scale(1.05);
  }
`;

const Steps = styled.ol`
  list-style: decimal;
  margin-left: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.lg};

  li {
    margin-bottom: ${theme.spacing.md};
    color: ${theme.colors.text.darkGray};
    line-height: 1.7;
    padding-left: ${theme.spacing.sm};

    strong {
      color: ${theme.colors.text.black};
      font-weight: ${theme.fontWeights.semibold};
    }
  }
`;

const WhatHappens = styled.div`
  margin-top: ${theme.spacing.xxl};
  padding-top: ${theme.spacing.xxl};
  border-top: 1px solid ${theme.colors.neutral[200]};
`;

const WhatHappensTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.black};
  margin-bottom: ${theme.spacing.lg};
`;

const WhatHappensList = styled.ul`
  list-style: disc;
  margin-left: ${theme.spacing.xl};
  text-align: left;

  li {
    margin-bottom: ${theme.spacing.sm};
    color: ${theme.colors.text.darkGray};
    line-height: 1.7;
  }
`;

const DeleteAccount = () => {
  return (
    <Section $padding="large">
      <Container>
        <Content>
          <h1>Delete Your Account</h1>
          <p>
            We're sorry to see you go. If you'd like to permanently delete your Lexifox account
            and all associated data, please follow the instructions below.
          </p>

          <WarningCard>
            <WarningHeader>
              <Warning size={32} weight="fill" />
              <WarningTitle>Important Warning</WarningTitle>
            </WarningHeader>
            <WarningText>
              Account deletion is <strong>permanent and cannot be reversed</strong>. All your
              learning progress, vocabulary data, statistics, and achievements will be
              permanently deleted and cannot be recovered.
            </WarningText>
          </WarningCard>

          <InstructionsCard>
            <InstructionTitle>
              <EnvelopeSimple size={28} weight="duotone" />
              How to Delete Your Account
            </InstructionTitle>

            <Steps>
              <li>
                Send an email to <strong>contact@lexifox.app</strong> from the email address
                associated with your Lexifox account
              </li>
              <li>
                Include "<strong>Account Deletion Request</strong>" in the subject line
              </li>
              <li>
                In the email body, confirm that you want to permanently delete your account
              </li>
              <li>
                We will process your request within <strong>7 business days</strong> and send
                you a confirmation email once your account has been deleted
              </li>
            </Steps>

            <EmailLink href="mailto:contact@lexifox.app?subject=Account%20Deletion%20Request">
              <EnvelopeSimple size={20} weight="fill" />
              Email contact@lexifox.app
            </EmailLink>

            <WhatHappens>
              <WhatHappensTitle>What will be deleted:</WhatHappensTitle>
              <WhatHappensList>
                <li>Your account information (email, username, profile)</li>
                <li>All vocabulary learning progress and statistics</li>
                <li>Words learned, practice history, and achievements</li>
                <li>Any premium subscriptions or purchases</li>
                <li>All app settings and preferences</li>
              </WhatHappensList>
            </WhatHappens>
          </InstructionsCard>

          <p style={{ fontSize: theme.fontSizes.sm, color: theme.colors.text.gray }}>
            If you're experiencing issues with the app or have feedback, please contact us at{' '}
            <a href="mailto:contact@lexifox.app" style={{ color: theme.colors.primary.main }}>
              contact@lexifox.app
            </a>
            {' '}before deleting your account. We'd love to help resolve any concerns.
          </p>
        </Content>
      </Container>
    </Section>
  );
};

export default DeleteAccount;
